import React, { useState, useEffect } from 'react';
import { usePageLoaderContext } from '../../../../../contexts/PageLoaderContext';
import { useNotification } from '../../../../../contexts/NotificationContext';
import apiService from '../../../../../services/api';
import GeneralSettings from './components/GeneralSettings';
import DangerZone from './components/DangerZone';
import styles from '../../../../../styles/SettingsTab.module.css';
import buttonStyles from '../../../../../styles/Buttons.module.css';

interface GroupDetailsData {
  id: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
    color: string;
  };
  team_lead: {
    id: string;
    full_name: string;
    email: string;
  };
  access_level: string;
  is_active: boolean;
  created_at: string;
  member_count: number;
  theme_color: string;
  logo: string | null;
}

interface SettingsTabProps {
  groupDetails: GroupDetailsData;
  onGroupUpdate: () => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ groupDetails, onGroupUpdate }) => {
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);

  // File upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: groupDetails.name,
    description: groupDetails.description,
    theme_color: groupDetails.theme_color || '#1a237e'
  });

  useEffect(() => {
    loadGroupSettings();
  }, []);

  const loadGroupSettings = async () => {
    try {
      const response = await apiService.getGroupSettings(groupDetails.id);
      if (response.status === 'success') {
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          name: data.name || groupDetails.name,
          description: data.description || groupDetails.description,
          theme_color: data.theme_color || groupDetails.theme_color || '#1a237e'
        }));

        // Set preview images if they exist
        if (data.logo_url) {
          setLogoPreview(data.logo_url);
        }
        if (data.banner_url) {
          setBannerPreview(data.banner_url);
        }
      }
    } catch (error) {
      console.error('Error loading group settings:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, theme_color: color }));
  };

  const handleFileUpload = (file: File, type: 'logo' | 'banner') => {
    const maxSize = type === 'logo' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;

    if (file.size > maxSize) {
      notificationService.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    if (!file.type.startsWith('image/')) {
      notificationService.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'logo') {
        setLogoFile(file);
        setLogoPreview(e.target?.result as string);
      } else {
        setBannerFile(file);
        setBannerPreview(e.target?.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (type: 'logo' | 'banner') => {
    if (type === 'logo') {
      setLogoFile(null);
      setLogoPreview(null);
    } else {
      setBannerFile(null);
      setBannerPreview(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    showLoader('Saving group settings...');

    try {
      const settingsData = {
        ...formData,
        logo: logoFile,
        banner: bannerFile
      };

      const response = await apiService.updateGroupSettings(groupDetails.id, settingsData);

      if (response.status === 'success') {
        notificationService.success('Group settings updated successfully');
        setShowSaveIndicator(true);
        setTimeout(() => setShowSaveIndicator(false), 3000);
        onGroupUpdate();
      } else {
        notificationService.error(response.message || 'Failed to update group settings');
      }
    } catch (error: any) {
      console.error('Error updating group settings:', error);
      notificationService.error(error.response?.data?.message || 'Failed to update group settings. Please try again.');
    } finally {
      setSaving(false);
      hideLoader();
    }
  };

  return (
    <div className={styles.settingsContainer}>

      {/* Actions Top */}
      <div className={styles.settingsActionsTop}>
        <button
          className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
          onClick={handleSaveSettings}
          disabled={saving}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
          </svg>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Tabs */}
      <div className={styles.settingsTabs}>
        <div
          className={`${styles.settingsTab} ${activeTab === 'general' ? styles.active : ''}`}
          onClick={() => handleTabChange('general')}
        >
          General
        </div>
        <div
          className={`${styles.settingsTab} ${activeTab === 'danger-zone' ? styles.active : ''}`}
          onClick={() => handleTabChange('danger-zone')}
        >
          Danger Zone
        </div>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className={`${styles.settingsContent} ${styles.active}`}>
          <GeneralSettings
            formData={formData}
            logoPreview={logoPreview}
            bannerPreview={bannerPreview}
            logoFile={logoFile}
            bannerFile={bannerFile}
            onInputChange={handleInputChange}
            onColorChange={handleColorChange}
            onFileUpload={handleFileUpload}
            onRemoveFile={removeFile}
            formatFileSize={formatFileSize}
          />
        </div>
      )}

      {/* Danger Zone Tab */}
      {activeTab === 'danger-zone' && (
        <div className={`${styles.settingsContent} ${styles.active}`}>
          <DangerZone
            groupId={groupDetails.id}
            groupName={formData.name}
            loading={loading}
            onGroupUpdate={onGroupUpdate}
          />
        </div>
      )}

      {/* Save Indicator */}
      <div className={`${styles.saveIndicator} ${showSaveIndicator ? styles.show : ''}`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
        Settings saved successfully!
      </div>
    </div>
  );
};

export default SettingsTab;

